/**
 * LA GARDE D'IMPRESSION ET D'EXPORTATION
 *
 * RÈGLE D'ALFRED (2026-09-13) : en lecture seule — après un essai non
 * converti — l'établissement peut VOIR son travail, mais ne peut ni l'écrire,
 * ni l'imprimer, ni l'exporter.
 *
 * CE QUE CETTE GARDE EST, ET CE QU'ELLE N'EST PAS. C'est un **contrôle
 * commercial**, pas une frontière de sécurité. Imprimer et exporter se font
 * dans le NAVIGATEUR, à partir de données que la RLS autorise à lire : un PDF
 * fabriqué par `html2canvas`, une capture d'écran ou un copier-coller ne
 * peuvent pas être empêchés côté serveur. La garde rend le geste indisponible
 * et explicable ; elle ne le rend pas impossible. Le prétendre serait mentir
 * sur ce que le produit tient.
 *
 * LE VERDICT RESTE SERVEUR. On n'interprète PAS `tenant_license_modules` ici :
 * la RPC `etat_commercial()` rapporte ce que `private.tenant_en_lecture_seule()`
 * a décidé. Recalculer l'état côté client donnerait deux décideurs pour une
 * même question, libres de diverger — le défaut évité deux fois le 2026-09-13.
 *
 * LE REPLI NE DOIT NI ACCORDER NI BLOQUER EN SILENCE. Si l'état est inconnu
 * (RPC en échec, session non chargée), la garde REFUSE — mais avec un motif
 * distinct, `etat_inconnu`, que l'appelant peut afficher. Un repli qui
 * accorderait donnerait le produit sur une simple panne de lecture ; un repli
 * qui bloquerait sans le dire ressemblerait à un bouton mort.
 */

/** Motifs de refus. Distincts, parce qu'ils appellent des messages distincts. */
export const MOTIF_REFUS = Object.freeze({
  LECTURE_SEULE: 'lecture_seule',
  ETAT_INCONNU: 'etat_inconnu',
});

/** Erreur levée par `exigerAutorisationExport`. Porte son motif. */
export class ExportRefuseError extends Error {
  constructor(motif) {
    super(
      motif === MOTIF_REFUS.LECTURE_SEULE
        ? "Export et impression indisponibles : l'abonnement est en lecture seule."
        : "Export et impression indisponibles : l'état de l'abonnement n'a pas pu être lu.",
    );
    this.name = 'ExportRefuseError';
    this.motif = motif;
  }
}

/**
 * L'état rapporté par le serveur. `null` tant qu'il n'a pas été chargé — et
 * `null` n'est PAS « autorisé » : voir `peutExporter`.
 */
let etat = null;

/** Rejoue l'état depuis le serveur. À appeler à l'ouverture de session. */
export async function chargerEtatCommercial(client) {
  try {
    const { data, error } = await client.rpc('etat_commercial');
    if (error) {
      etat = null;
      return null;
    }
    etat = data ?? null;
    return etat;
  } catch {
    etat = null;
    return null;
  }
}

/**
 * HORS CONTEXTE TENANT — Master global, page publique.
 *
 * On le pose SANS interroger le serveur, et ce n'est pas une décision
 * commerciale prise côté client : c'est un fait que le client détient déjà —
 * il n'y a pas de tenant. Le serveur répondrait exactement cela
 * (`get_request_tenant_id()` est nul), au prix d'un aller-retour inutile à
 * chaque ouverture du shell Master.
 */
export function definirEtatHorsTenant() {
  etat = { contexte_tenant: false };
  return etat;
}

/** Pour les tests et la déconnexion : repart d'un état non chargé. */
export function reinitialiserEtatCommercial() {
  etat = null;
}

/** L'état tel qu'il a été rapporté, sans interprétation. */
export function etatCommercial() {
  return etat;
}

/**
 * L'export et l'impression sont-ils permis ?
 *
 * Rend `true`, `false`, ou **`null` quand on ne sait pas** — jamais `false`
 * pour dire « inconnu ». Un appelant qui teste `if (peutExporter())` traite
 * donc `null` comme un refus, ce qui est le comportement voulu, tandis qu'un
 * appelant qui veut DISTINGUER le sait par `=== null`.
 */
export function peutExporter() {
  if (etat === null || typeof etat !== 'object') return null;
  // Hors contexte tenant — Master global, page publique — la question ne se
  // pose pas : rien n'est en lecture seule.
  if (etat.contexte_tenant === false) return true;
  if (typeof etat.lecture_seule !== 'boolean') return null;
  return !etat.lecture_seule;
}

/** Le motif du refus, ou `null` si l'export est permis. */
export function motifRefusExport() {
  const permis = peutExporter();
  if (permis === true) return null;
  return permis === null ? MOTIF_REFUS.ETAT_INCONNU : MOTIF_REFUS.LECTURE_SEULE;
}

/**
 * À appeler AVANT tout export ou impression. Lève `ExportRefuseError` si le
 * geste n'est pas permis — un refus qui LÈVE est visible ; un refus qui rend
 * `false` en silence produit un bouton qui ne fait rien, et personne ne sait
 * pourquoi.
 */
export function exigerAutorisationExport() {
  const motif = motifRefusExport();
  if (motif) throw new ExportRefuseError(motif);
}
